import { supabase } from './client';
import { getCurrentEmpresaId } from './auth-utils';

export interface CaixaSession {
  id: string;
  empresa_id: string;
  data_abertura: string;
  data_fechamento: string | null;
  saldo_inicial: number;
  saldo_final: number | null;
  status: 'aberto' | 'fechado';
  observacoes: string | null;
  created_at: string;
  updated_at: string;
}

// Abrir caixa
export async function abrirCaixa(saldoInicial: number, observacoes?: string): Promise<{ ok: boolean; id?: string; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) return { ok: false, error: 'Empresa não identificada' };

    const hoje = new Date().toISOString().split('T')[0];

    // Verificar se já existe caixa aberto hoje
    const { data: existente } = await supabase
      .from('caixa_sessions')
      .select('id')
      .eq('empresa_id', empresaId)
      .eq('status', 'aberto')
      .gte('data_abertura', `${hoje}T00:00:00`)
      .lte('data_abertura', `${hoje}T23:59:59`)
      .single();

    if (existente) {
      return { ok: false, error: 'Caixa já está aberto hoje' };
    }

    const { data, error } = await supabase
      .from('caixa_sessions')
      .insert({
        empresa_id: empresaId,
        data_abertura: new Date().toISOString(),
        saldo_inicial: saldoInicial,
        status: 'aberto',
        observacoes: observacoes || null
      })
      .select('id')
      .single();

    if (error) throw error;

    return { ok: true, id: data?.id };
  } catch (error: any) {
    console.error('Erro ao abrir caixa:', error);
    return { ok: false, error: error.message };
  }
}

// Fechar caixa
export async function fecharCaixa(saldoFinal: number, observacoes?: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) return { ok: false, error: 'Empresa não identificada' };

    const hoje = new Date().toISOString().split('T')[0];

    // Buscar caixa aberto hoje
    const { data: caixa, error: errorBusca } = await supabase
      .from('caixa_sessions')
      .select('id')
      .eq('empresa_id', empresaId)
      .eq('status', 'aberto')
      .gte('data_abertura', `${hoje}T00:00:00`)
      .lte('data_abertura', `${hoje}T23:59:59`)
      .single();

    if (!caixa) {
      return { ok: false, error: 'Nenhum caixa aberto encontrado' };
    }

    const { error } = await supabase
      .from('caixa_sessions')
      .update({
        data_fechamento: new Date().toISOString(),
        saldo_final: saldoFinal,
        status: 'fechado',
        observacoes: observacoes || null
      })
      .eq('id', caixa.id);

    if (error) throw error;

    return { ok: true };
  } catch (error: any) {
    console.error('Erro ao fechar caixa:', error);
    return { ok: false, error: error.message };
  }
}

// Buscar caixa aberto de hoje
export async function buscarCaixaAbertoHoje(): Promise<CaixaSession | null> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) return null;

    const hoje = new Date().toISOString().split('T')[0];

    const { data } = await supabase
      .from('caixa_sessions')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('status', 'aberto')
      .gte('data_abertura', `${hoje}T00:00:00`)
      .lte('data_abertura', `${hoje}T23:59:59`)
      .single();

    return data as CaixaSession | null;
  } catch (error) {
    console.error('Erro ao buscar caixa:', error);
    return null;
  }
}

// Histórico de sessões
export async function listarHistoricoCaixa(dias: number = 30): Promise<CaixaSession[]> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) return [];

    const dataLimite = new Date();
    dataLimite.setDate(dataLimite.getDate() - dias);

    const { data } = await supabase
      .from('caixa_sessions')
      .select('*')
      .eq('empresa_id', empresaId)
      .gte('created_at', dataLimite.toISOString())
      .order('data_abertura', { ascending: false });

    return (data || []) as CaixaSession[];
  } catch (error) {
    console.error('Erro ao buscar histórico:', error);
    return [];
  }
}
